import styles from "./Ticker.module.css";

/** A plain line, or one that links out. */
export type TickerItem = string | { text: string; href: string };

const textOf = (item: TickerItem) => (typeof item === "string" ? item : item.text);

interface Props {
  items: readonly TickerItem[];
  /** Accessible name for the strip. */
  label?: string;
  /**
   * How many times to repeat the list within a single run. Raise it when the
   * list is short enough that one pass doesn't fill the viewport, or the loop
   * shows a gap. Purely visual: screen readers still hear the list once.
   */
  repeat?: number;
}

/**
 * §03 broadcast ticker.
 *
 * The content rule matters more than the animation: this strip never carries a
 * price, line or implied probability. It's the surface most likely to be read as
 * live market data, so the one thing it must never do is imply that one exists.
 * See src/content/ticker.ts.
 *
 * The run is duplicated and the track translates -50%, which is what makes the
 * loop seamless. aria-hidden on the copy so screen readers hear the list once.
 *
 * That -50% assumes ONE run is at least as wide as the viewport. A short list on
 * a wide screen leaves a visible gap mid-loop, so `repeat` pads the run out.
 */
export function Ticker({ items, label = "Status", repeat = 1 }: Props) {
  /*
   * `decorative` is why this is a function rather than one array rendered twice.
   *
   * The second run is aria-hidden to stop screen readers reading the list twice.
   * A link inside an aria-hidden subtree is still keyboard-focusable, which is
   * the classic version of this bug: you tab into an element the accessibility
   * tree says isn't there, and a screen reader announces nothing. So the copy
   * renders links as inert spans, and only the real run gets anchors.
   *
   * Index is part of the key on purpose: with repeat > 1 the same string appears
   * more than once and the text alone would collide.
   */
  const cellsFor = (decorative: boolean) =>
    Array.from({ length: repeat }, (_, pass) =>
      items.map((item, i) => {
        const text = textOf(item);
        const linked = typeof item !== "string" && !decorative;

        return (
          <span key={`${pass}-${i}-${text}`} className={styles.item}>
            {linked ? (
              <a
                className={styles.link}
                href={(item as { href: string }).href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {text}
              </a>
            ) : (
              text
            )}
            <span className={styles.sep} aria-hidden="true">
              ·
            </span>
          </span>
        );
      }),
    ).flat();

  return (
    <div className={styles.strip} aria-label={label} role="marquee">
      <div className={styles.track}>
        <div className={styles.run}>{cellsFor(false)}</div>
        <div aria-hidden="true" className={styles.run}>
          {cellsFor(true)}
        </div>
      </div>
    </div>
  );
}
